import { differenceInMinutes } from 'date-fns';
import { useCallback } from 'react';

const MIN_MINUTES_BEFORE_APPOINTMENT = 3;
const MAX_MINUTES_BEFORE_APPOINTMENT = 5;

const defaultGetNow = () => new Date();

/**
 * Hook that provides a callback to check if the next upcoming appointment falls
 * starts within the transcription consent display window (≥3 minutes and ≤5
 * minutes from now).
 *
 * @param nextAppointment - The appointment to check
 * @param getNow - Optional override for getting the current date (for testing)
 * @returns A callback that returns whether the next upcoming appointment starts
 * within the transcription consent display window.
 */
export function useAppointmentWithinConsentWindowCallback(
  nextAppointment: { timeStart: string } | null,
  getNow: () => Date = defaultGetNow,
): () => boolean {
  const timeStart = nextAppointment?.timeStart;

  return useCallback(() => {
    if (!timeStart) return false;
    const startDate = new Date(timeStart);
    const minutesUntilStart = differenceInMinutes(startDate, getNow());

    return minutesUntilStart >= MIN_MINUTES_BEFORE_APPOINTMENT && minutesUntilStart <= MAX_MINUTES_BEFORE_APPOINTMENT;
  }, [timeStart, getNow]);
}
